/**
 * Defects: open defects grouped by severity (critical first). The group header says the
 * severity and the count once; each row has its status, key, title, one meta line (what it
 * affects, its age) and the owner when owners differ. Suggested defects from runtime failures
 * are one link away.
 */
import { Bug, Hand, Siren } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { SEVERITY_LABEL } from '@orgus/protocol';
import type { BacklogItem, DefectSignal, Id, ItemLink, QualityIssue } from '@orgus/protocol';
import { Button, EmptyState, Pressable, RelativeTime, cn, plural } from '@orgus/ui';
import { headlineMission } from '../missions/model.ts';
import type { MissionRow } from '../missions/model.ts';
import { ItemState, StatePill } from '../missions/parts.tsx';
import { affectedBy, defectGroups, isConstantColumn } from './model.ts';
import { KeyLabel, SeverityMark } from './parts.tsx';

export function DefectsView({
  items,
  allItems,
  links,
  rows,
  signals,
  showClosed,
  selectedId,
  onOpen,
  onReviewSignals,
  onNewDefect,
}: {
  items: readonly BacklogItem[];
  allItems: readonly BacklogItem[];
  links: readonly ItemLink[];
  rows: readonly MissionRow[];
  issues?: ReadonlyMap<Id, QualityIssue[]>;
  signals: readonly DefectSignal[];
  showClosed: boolean;
  onShowClosed?: (v: boolean) => void;
  selectedId: Id | null;
  onOpen: (id: Id) => void;
  onReviewSignals: () => void;
  onNewDefect: () => void;
}) {
  const groups = useMemo(() => defectGroups(items, showClosed), [items, showClosed]);
  const byId = useMemo(() => new Map(allItems.map((i) => [i.id, i])), [allItems]);
  const openSignals = signals.filter((s) => s.status === 'open');
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const showOwner = !isConstantColumn(groups.flatMap((g) => g.items.map((d) => d.owner ?? '')));

  return (
    <div className="flex flex-col gap-3">
      {openSignals.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface px-4 py-2 text-ui text-fg-muted">
          <span className="flex items-center gap-2">
            <Siren className="size-4 text-fg-subtle" aria-hidden />
            {plural(openSignals.length, 'failure')} seen at runtime may be {openSignals.length === 1 ? 'a defect' : 'defects'}.
          </span>
          <Button variant="secondary" size="sm" onClick={onReviewSignals}>
            Review suggestions
          </Button>
        </div>
      )}
      {!total ? (
        <div className="rounded-card border border-border bg-surface">
          <EmptyState
            icon={<Bug />}
            title={showClosed ? 'No defects' : 'No open defects'}
            description={openSignals.length ? 'Failures were seen, though: review the suggested defects.' : 'Defects you report, and the ones you accept from runtime failures, show here by severity.'}
            action={
              <Button variant="secondary" icon={<Bug />} onClick={onNewDefect}>
                Report a defect
              </Button>
            }
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-surface" data-list>
          {groups.map((g) => (
            <section key={g.severity} aria-label={`${SEVERITY_LABEL[g.severity]}, ${g.items.length}`}>
              {groups.length > 1 && (
                <header className="flex h-row items-center gap-2 border-b border-border-subtle bg-raised px-4" data-group-header>
                  <SeverityMark severity={g.severity} />
                  <span className="text-caption text-fg-subtle num">{g.items.length}</span>
                </header>
              )}
              <ul>
                {g.items.map((d) => {
                  const affects = affectedBy(d.id, links, byId);
                  const waiting = headlineMission(rows, d.id)?.view.state === 'waiting';
                  return (
                    <li key={d.id} className="relative border-b border-border-subtle last:border-b-0">
                      <Pressable data-row onClick={() => onOpen(d.id)} className={cn('flex h-row items-center gap-3 px-4 hover:bg-hover', selectedId === d.id && 'selected-tint')}>
                        <span className="flex w-8 shrink-0 sm:w-32" data-column="status">
                          {waiting ? <StatePill tone="attention" icon={Hand} label="Needs you" variant="soft" className="max-sm:[&>span:last-child]:sr-only" /> : <ItemState status={d.status} kind="defect" className="max-sm:[&>span:last-child]:sr-only" />}
                        </span>
                        <KeyLabel value={d.key} className="hidden w-20 sm:inline" />
                        <span className="flex min-w-0 flex-1 items-baseline gap-2">
                          <span className="truncate text-ui text-fg">{d.title}</span>
                          {affects[0] && (
                            <span className="hidden min-w-0 truncate text-caption text-fg-subtle lg:inline" data-meta>
                              Affects {affects[0].key}
                              {affects.length > 1 ? ` and ${affects.length - 1} more` : ''}
                            </span>
                          )}
                        </span>
                        {showOwner && (
                          <span className="hidden w-24 shrink-0 truncate text-caption text-fg-muted md:block" data-column="owner">
                            {d.owner ?? ''}
                          </span>
                        )}
                        <span className="hidden w-20 shrink-0 text-right text-caption text-fg-subtle sm:block" data-meta>
                          <RelativeTime ts={d.createdAt} />
                        </span>
                      </Pressable>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
